import { AlertTriangle, Check, Pencil, RefreshCw } from 'lucide-react'
import { HelpIcon, ApiError, Button } from '../../core/index.js'
import type { DraftException, DraftFlag, DraftSchedule, PtoDecision, ShiftPreferences } from '../types.js'
import { PRIORITY_CLASS, PRIORITY_LABELS, SHIFT_TYPE_LABELS, datesOf, describeDay, describePeriod, firstWeekdayOf } from './preferencesText.js'

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

const FLAG_TEXT: Record<DraftFlag, string> = {
  under_target: 'Below your minimum: not enough published shifts fit your preferences. Your scheduler sees this.',
  used_low_day: 'A Low priority day off was used to reach your minimum.',
  used_medium_day: 'A Medium priority day off was used to reach your minimum.',
  nothing_published: 'Your facility has not published shifts for this month yet, so there is nothing to draft from.',
  assigned: 'Your tier is assigned its month rather than asked: weekends first, then weekdays, by the facility’s usual shift for each day.',
  medium_day_override: 'Your tier’s rule turns some Medium priority days off into working days.',
  pto_day_denied: 'Your tier’s rule denies the first PTO day asked for; it is a working day.',
  weekly_overtime: 'A week goes past the facility’s overtime line. It is placed, and your manager sees it.',
  daily_overtime: 'A shift is longer than the facility’s daily overtime line.',
  day_off_taken: 'A day off was taken to cover a seat your unit was short on. Low days go first, then Medium, then High, and the lowest-ranked bidder off that day covers it.',
}

const PTO_STATUS: Record<PtoDecision['status'], string> = { approved: 'approved', denied: 'denied - a working day', requested: 'requested; your facility decides' }

/** The notes the rules left that the flags do not already say: reviews for the manager and the floor. */
function exceptionsToShow(exceptions: readonly DraftException[]): DraftException[] {
  return exceptions.filter((entry) => entry.severity !== 'flag')
}

/** "20 shifts · 160 hrs". */
export function describeDraft(draft: Pick<DraftSchedule, 'assignments' | 'hours'>): string {
  return `${draft.assignments.length} ${draft.assignments.length === 1 ? 'shift' : 'shifts'} · ${draft.hours} hrs`
}

/**
 * The draft as the worker reviews it (the demo's "Here's your draft
 * schedule"): the month with a shift type on each working day, the hours,
 * what the engine had to do, then Submit as-is or Make changes. A draft
 * claims nothing until the scheduler builds the month.
 */
/** What a day with no shift on the draft was, as the worker asked or the calendar says: before the draft's window, PTO, or a day off at its priority. */
function restDay(date: string, draft: DraftSchedule, asked: Pick<ShiftPreferences, 'ptoDates' | 'daysOff' | 'availableDates'> | null | undefined): { label: string; className: string; title: string } | null {
  if (draft.window && date < draft.window.from) {
    return { label: 'past', className: 'border-dashed border-marine/15 bg-white text-ink-mid/50', title: 'Before today: not drafted' }
  }
  if (asked?.ptoDates.includes(date)) {
    return { label: 'PTO', className: 'border-marine/40 bg-marine/10 text-marine', title: 'PTO asked for' }
  }
  // A worker who marks the days they can work: a free day they marked is open, any other is theirs.
  const available = asked?.availableDates ?? null
  if (available !== null) {
    return available.includes(date) ? { label: 'open', className: 'border-dashed border-green/50 bg-white text-green', title: 'A day you can work, not needed' } : null
  }
  const off = asked?.daysOff.find((entry) => entry.date === date)
  return off ? { label: PRIORITY_LABELS[off.priority], className: PRIORITY_CLASS[off.priority], title: `${PRIORITY_LABELS[off.priority]} priority day off` } : null
}

/** "Drafted for the 16 days left of October, from Oct 16: at least 66 of your 128 hours." for a month already running; nothing for a whole month. */
function describeWindow(draft: DraftSchedule, askedMin: number | null): string | null {
  const window = draft.window
  if (!window || window.days >= window.ofDays) {
    return null
  }
  return `Drafted for the ${window.days} days left of ${describePeriod(draft.period)}, from ${describeDay(window.from)}: the minimum is that share of the month, ${window.minHours} hours${askedMin !== null ? ` of your ${askedMin}` : ''}.`
}

export function DraftScheduleReview({ draft, isStale, pending, error, onAccept, onChange, onRebuild, asked }: { draft: DraftSchedule; isStale: boolean; pending: boolean; error: unknown; onAccept: () => void; onChange: () => void; /** Builds the draft again from the saved preferences, offered while it is out of date. */ onRebuild?: () => void; asked?: Pick<ShiftPreferences, 'ptoDates' | 'daysOff' | 'hours' | 'availableDates'> | null }) {
  const byDate = new Map(draft.assignments.map((entry) => [entry.date, entry]))
  const taken = new Map((draft.takenDaysOff ?? []).map((entry) => [entry.date, entry]))
  const dates = datesOf(draft.period)
  const lead = firstWeekdayOf(draft.period)
  const accepted = draft.status === 'accepted'
  const assigned = draft.flags.includes('assigned')
  // "Nothing published" is no longer true of a draft that is out of date - shifts arriving is what made it so.
  const shownFlags = isStale ? draft.flags.filter((flag) => flag !== 'nothing_published') : draft.flags
  const overrides = new Set(draft.overrides ?? [])
  const denied = new Set((draft.ptoDecisions ?? []).filter((entry) => entry.status === 'denied').map((entry) => entry.date))
  const notes = exceptionsToShow(draft.exceptions ?? [])
  const failure = error ? (error instanceof ApiError ? error.message : 'That could not be saved. Try again.') : null
  return (
    <div className="flex flex-col gap-3">
      {isStale && (
        <section role="alert" className="border-2 border-coral bg-coral-lt p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-coral" />
            <div className="min-w-0">
              <h2 className="text-sm font-extrabold text-ink">This draft is out of date</h2>
              <p className="mt-0.5 text-[11px] text-ink leading-snug">Your preferences, your facility’s rules or its published shifts changed since this was built. Build it again to match them.</p>
              <p className="mt-1 text-[11px] text-ink-mid leading-snug">
                Building again drafts {describePeriod(draft.period)} afresh from {assigned ? 'your facility’s rules' : 'your saved preferences'} and the shifts published now, and replaces the draft below. Nothing goes to your scheduler until you submit it.
              </p>
              {onRebuild && (
                <Button icon={RefreshCw} size="sm" className="mt-2" disabled={pending} onClick={onRebuild}>
                  {pending ? 'Building…' : 'Build again'}
                </Button>
              )}
              {/* Why a build was refused, beside the button that asked - not at the foot of the page. */}
              {failure && (
                <p role="alert" className="mt-2 text-[11px] font-bold text-coral">
                  {failure}
                </p>
              )}
            </div>
          </div>
        </section>
      )}
      <section className="bg-white border border-marine/15 p-4">
        <h2 className="flex items-center gap-1 text-sm font-extrabold text-ink">
          {accepted ? 'Your schedule is submitted' : assigned ? 'Here’s your assigned schedule' : "Here's your draft schedule"}
          <HelpIcon topic={assigned ? 'schedule.worker-assigned' : 'schedule.worker-draft'} />
        </h2>
        <p className="mt-0.5 text-[11px] text-ink-mid leading-snug">
          {accepted ? `Submitted for ${describePeriod(draft.period)}. Your scheduler builds the month from it and tells you what changed.` : assigned ? 'Built by the facility’s rules for your tier: every weekend first, then weekdays, by the usual shift for each day. Nothing is yours until the scheduler builds the month.' : 'Built from your preferences under your facility’s rules: your highest-ranked shifts first, one a day, spread across the month, never on a PTO day or a High priority day off. Nothing is yours until the scheduler builds the month.'}
        </p>
        {describeWindow(draft, asked?.hours.min ?? null) && <p className="mt-1.5 text-[11px] text-ink-mid">{describeWindow(draft, asked?.hours.min ?? null)}</p>}
        <div className={`mt-2 flex items-center justify-between border px-2.5 py-1.5 text-[11px] ${draft.flags.includes('under_target') ? 'border-coral/50 bg-coral-lt' : 'border-green/50 bg-green/10'}`}>
          <span className="text-ink-mid">{describeDraft(draft)}</span>
          <span className={`font-bold ${draft.flags.includes('under_target') ? 'text-coral' : 'text-green'}`}>{draft.flags.includes('under_target') ? 'Under target' : 'On target'}</span>
        </div>
        <div className="mt-3">
          <div className="grid grid-cols-7 gap-1 text-center text-[9px] font-bold uppercase tracking-wide text-ink-mid">
            {WEEKDAYS.map((day) => (
              <div key={day}>{day}</div>
            ))}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-1">
            {Array.from({ length: lead }, (_, index) => (
              <div key={`lead-${index}`} />
            ))}
            {dates.map((date) => {
              const shift = byDate.get(date)
              const usedOff = draft.usedDaysOff.find((entry) => entry.date === date)
              const ruled = overrides.has(date) || denied.has(date) || taken.has(date)
              const rest = shift ? null : restDay(date, draft, asked)
              const tone = !shift ? (rest?.className ?? 'border-marine/10 bg-canvas text-ink-mid') : ruled ? 'border-coral bg-coral-lt text-ink' : usedOff ? 'border-gold bg-gold/30 text-ink' : 'border-marine bg-marine text-white'
              return (
                <div key={date} className={`flex h-11 flex-col items-center justify-center rounded border text-[11px] font-bold ${tone}`} title={shift ? `${SHIFT_TYPE_LABELS[shift.shiftType]} ${shift.startTime}–${shift.endTime}${overrides.has(date) ? ' · overridden day off' : denied.has(date) ? ' · PTO denied' : taken.has(date) ? ` · taken for coverage (${PRIORITY_LABELS[taken.get(date)!.priority]} day off)` : ''}` : rest?.title}>
                  <span>{Number(date.slice(-2))}</span>
                  {shift && <span className="text-[8px] font-bold uppercase leading-none">{SHIFT_TYPE_LABELS[shift.shiftType]}</span>}
                  {rest && <span className="text-[8px] font-bold uppercase leading-none">{rest.label}</span>}
                </div>
              )
            })}
          </div>
        </div>
        {asked && (asked.daysOff.length > 0 || asked.ptoDates.length > 0 || (draft.window?.days ?? 0) < (draft.window?.ofDays ?? 0)) && (
          <div className="mt-2 flex flex-wrap gap-1.5 text-[9px] font-bold uppercase" aria-label="Key">
            <span className="rounded border border-marine bg-marine px-1.5 py-0.5 text-white">Shift</span>
            {asked.ptoDates.length > 0 && <span className="rounded border border-marine/40 bg-marine/10 px-1.5 py-0.5 text-marine">PTO</span>}
            {(['high', 'medium', 'low'] as const).filter((priority) => asked.daysOff.some((entry) => entry.priority === priority)).map((priority) => (
              <span key={priority} className={`rounded border px-1.5 py-0.5 ${PRIORITY_CLASS[priority]}`}>{PRIORITY_LABELS[priority]} day off</span>
            ))}
            {draft.window && draft.window.days < draft.window.ofDays && <span className="rounded border border-dashed border-marine/15 px-1.5 py-0.5 text-ink-mid/60">Past</span>}
          </div>
        )}
        {shownFlags.length > 0 && (
          <ul className="mt-3 flex flex-col gap-1 text-[11px] text-ink leading-snug">
            {shownFlags.map((flag) => (
              <li key={flag} className={flag === 'under_target' || flag === 'nothing_published' ? 'text-coral' : 'text-ink-mid'}>
                {FLAG_TEXT[flag]}
                {(flag === 'used_low_day' || flag === 'used_medium_day') && (
                  <>
                    {' '}
                    ({draft.usedDaysOff.filter((entry) => entry.priority === (flag === 'used_low_day' ? 'low' : 'medium')).map((entry) => `${describeDay(entry.date)} (${PRIORITY_LABELS[entry.priority]})`).join(', ')})
                  </>
                )}
                {flag === 'day_off_taken' && (
                  <>
                    {' '}
                    ({(draft.takenDaysOff ?? []).map((entry) => `${describeDay(entry.date)} (${PRIORITY_LABELS[entry.priority]})`).join(', ')})
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        {(draft.ptoDecisions ?? []).length > 0 && (
          <ul className="mt-3 flex flex-col gap-0.5 text-[11px] text-ink leading-snug" aria-label="PTO">
            {(draft.ptoDecisions ?? []).map((entry) => (
              <li key={entry.date} className={entry.status === 'denied' ? 'text-coral' : 'text-ink-mid'}>
                PTO {describeDay(entry.date)}: {PTO_STATUS[entry.status]}
              </li>
            ))}
          </ul>
        )}
        {notes.length > 0 && (
          <ul className="mt-3 flex flex-col gap-0.5 text-[11px] text-ink-mid leading-snug" aria-label="For your manager">
            {notes.map((entry, index) => (
              <li key={`${entry.code}-${entry.date ?? index}`}>
                {entry.severity === 'review' ? 'For your manager: ' : ''}
                {entry.message}
              </li>
            ))}
          </ul>
        )}
        {failure && !isStale && (
          <p role="alert" className="mt-2 text-[11px] text-coral">
            {failure}
          </p>
        )}
      </section>
      <div className="flex flex-wrap justify-end gap-2">
        <Button icon={Pencil} variant="secondary" disabled={pending} onClick={onChange}>
          {assigned ? 'Build again' : 'Make changes'}
        </Button>
        {/* An out-of-date draft is built again from the notice at the top, never submitted. */}
        {!accepted && !(isStale && onRebuild) && (
          <Button icon={Check} disabled={pending || isStale} onClick={onAccept}>
            {pending ? 'Submitting…' : 'Submit as-is'}
          </Button>
        )}
      </div>
    </div>
  )
}
